import React, { useState } from 'react';
import {
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, PolicyTypes, RescheduleFeeTierData } from '../api';
import { ApiError } from '../api/core/ApiError';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';

type ReschedulePolicySettingsProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (open: boolean) => void;
};

type FeeTierRow = {
  key: string;
  hours_before: number | '';
  policy_type: PolicyTypes;
  value: number | '';
};

const rowsFromAccount = (account: AccountData): FeeTierRow[] =>
  (account?.reschedule_policy?.fee_tiers ?? []).map((tier: RescheduleFeeTierData, index: number) => ({
    key: `saved-${index}`,
    hours_before: tier.hours_before,
    policy_type: tier.policy_type,
    value: tier.value,
  }));

const parseNumberInput = (rawValue: string): number | '' => (rawValue === '' ? '' : Number(rawValue));

export const ReschedulePolicySettings = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: ReschedulePolicySettingsProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const [enabled, setEnabled] = useState<boolean>(account?.reschedule_policy?.enabled ?? false);
  const [minHoursBefore, setMinHoursBefore] = useState<number>(account?.reschedule_policy?.min_hours_before ?? 0);
  const [isGroupSizeLimited, setIsGroupSizeLimited] = useState<boolean>(
    account?.reschedule_policy?.max_persons != null,
  );
  const [maxPersons, setMaxPersons] = useState<number | ''>(account?.reschedule_policy?.max_persons ?? '');
  const [rows, setRows] = useState<FeeTierRow[]>(() => rowsFromAccount(account));
  const [newRowCounter, setNewRowCounter] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const hoursError = (row: FeeTierRow) => {
    if (row.hours_before === '') return t('accountSettings.reschedulePolicy.errors.required');
    if (row.hours_before < 1) return t('accountSettings.reschedulePolicy.errors.required');
    if (rows.filter((other) => other.hours_before === row.hours_before).length > 1) {
      return t('accountSettings.reschedulePolicy.errors.duplicate');
    }
    return undefined;
  };

  const valueError = (row: FeeTierRow) => {
    if (row.value === '' || row.value < 0) return t('accountSettings.reschedulePolicy.errors.required');
    if (row.policy_type === PolicyTypes.PERCENT && row.value > 100) {
      return t('accountSettings.reschedulePolicy.errors.percentRange');
    }
    return undefined;
  };

  const maxPersonsError =
    isGroupSizeLimited && (maxPersons === '' || maxPersons < 1)
      ? t('accountSettings.reschedulePolicy.errors.required')
      : undefined;

  const hasInvalidRow = rows.some((row) => !!hoursError(row) || !!valueError(row)) || !!maxPersonsError;

  const addRow = () => {
    setRows((current) => [
      ...current,
      { key: `new-${newRowCounter}`, hours_before: '', policy_type: PolicyTypes.PERCENT, value: '' },
    ]);
    setNewRowCounter((current) => current + 1);
  };

  const updateRow = (key: string, changes: Partial<FeeTierRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));

  const deleteRow = (key: string) => setRows((current) => current.filter((row) => row.key !== key));

  const save = async () => {
    setIsSaving(true);
    try {
      const updatedAccount: AccountData = await safeExecute({
        apiCall: () =>
          AccountService.updateReschedulePolicy({
            enabled,
            min_hours_before: minHoursBefore,
            fee_tiers: rows.map((row) => ({
              hours_before: row.hours_before as number,
              policy_type: row.policy_type,
              value: row.value as number,
            })),
            max_persons: isGroupSizeLimited ? (maxPersons as number) : undefined,
          }),
        successMessage: t('accountSettings.reschedulePolicy.saved'),
        onErrorMessageGenerator: (error: ApiError) => {
          const detail = error.body?.detail;
          return typeof detail === 'string' ? detail : t('api.error.default');
        },
      });
      await updateLocalAccount(updatedAccount);
      setRows(rowsFromAccount(updatedAccount));
      setIsUpdatedSnackOpen(true);
    } catch {
      return;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" gap={2} maxWidth={600}>
      <Typography variant="h6">{t('tabs.reschedulepolicy')}</Typography>
      <FormControlLabel
        control={<Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />}
        label={t('accountSettings.reschedulePolicy.enabled')}
      />
      <TextField
        size="small"
        type="number"
        disabled={!enabled}
        label={t('accountSettings.reschedulePolicy.minHoursBefore')}
        value={minHoursBefore}
        onChange={(event) => setMinHoursBefore(Number(event.target.value))}
        inputProps={{ min: 0 }}
      />
      <Grid container alignItems="center" gap={2} wrap="nowrap">
        <FormControlLabel
          disabled={!enabled}
          control={
            <Switch checked={isGroupSizeLimited} onChange={(event) => setIsGroupSizeLimited(event.target.checked)} />
          }
          label={t('accountSettings.reschedulePolicy.limitGroupSize')}
        />
        {isGroupSizeLimited && (
          <TextField
            size="small"
            type="number"
            disabled={!enabled}
            label={t('accountSettings.reschedulePolicy.maxPersons')}
            value={maxPersons}
            onChange={(event) => setMaxPersons(parseNumberInput(event.target.value))}
            error={!!maxPersonsError}
            helperText={maxPersonsError ?? t('accountSettings.reschedulePolicy.maxPersonsHelp')}
            inputProps={{ min: 1 }}
          />
        )}
      </Grid>
      {rows.map((row) => (
        <Grid key={row.key} container alignItems="flex-start" gap={1} wrap="nowrap">
          <TextField
            size="small"
            type="number"
            disabled={!enabled}
            label={t('accountSettings.reschedulePolicy.hoursBefore')}
            value={row.hours_before}
            onChange={(event) => updateRow(row.key, { hours_before: parseNumberInput(event.target.value) })}
            error={!!hoursError(row)}
            helperText={hoursError(row)}
            inputProps={{ min: 1 }}
          />
          <Select
            size="small"
            disabled={!enabled}
            value={row.policy_type}
            onChange={(event) => updateRow(row.key, { policy_type: event.target.value as PolicyTypes })}
            inputProps={{ 'aria-label': t('accountSettings.reschedulePolicy.type') }}
            sx={{ minWidth: 120 }}>
            <MenuItem value={PolicyTypes.PERCENT}>{t('accountSettings.reschedulePolicy.percent')}</MenuItem>
            <MenuItem value={PolicyTypes.FIXED}>{t('accountSettings.reschedulePolicy.fixed')}</MenuItem>
          </Select>
          <TextField
            size="small"
            type="number"
            disabled={!enabled}
            label={t('accountSettings.reschedulePolicy.value')}
            value={row.value}
            onChange={(event) => updateRow(row.key, { value: parseNumberInput(event.target.value) })}
            error={!!valueError(row)}
            helperText={valueError(row)}
            inputProps={{ min: 0 }}
          />
          <IconButton aria-label={t('delete')} disabled={!enabled} onClick={() => deleteRow(row.key)}>
            <Delete />
          </IconButton>
        </Grid>
      ))}
      <Grid container justifyContent="space-between" alignItems="center">
        <Button startIcon={<Add />} disabled={!enabled} onClick={addRow}>
          {t('accountSettings.reschedulePolicy.addTier')}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isSaving}
          disabled={hasInvalidRow}
          onClick={save}
          sx={{ boxShadow: 'none' }}>
          {t('save')}
        </LoadingButton>
      </Grid>
    </Grid>
  );
};
